import {
  Box,
  InputGroup as ChakraInputGroup,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  InputLeftElement,
  Switch,
  Tooltip
} from '@chakra-ui/react'
import type { InsuranceParamsPropertyDto } from 'BrokerTsLib/src/dtos'
import { Insurer, Risk, RiskVariant, Selection } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useContext } from 'react'

import { Datepicker } from '../../../components/Datepicker'
import { FormLabel } from '../../../components/FormLabel'
import { Loading } from '../../../components/Loading'
import { SwitchControl } from '../../../components/SwitchControl'
import { useGetInsurersSpecificParams } from '../../../hooks/useInsuranceParams'
import { Calendar, LabelHintIcon } from '../../../icons'
import { insuranceParamsContext } from '../../../layouts/Wizard'
import { BaScoringCheckbox } from '../BAScoringCheckbox'
import { IndividualCompany } from '../IndividualCompany'
import {
  IndividualDiscountsGroup,
  IndividualDiscountsWrapper,
  LocalInputGroup,
} from '../IndividualDiscountsWrapper'

export const IndividualDiscounts = () => {
  const { insuranceParamsId: paramsId, insuranceParams: ip } = useContext(
    insuranceParamsContext,
  )!
  const insuranceParams = ip as InsuranceParamsPropertyDto
  const isContracted = !!insuranceParams?.generalParams.contractNumber

  const { setValue, getValues, register, watch, getFieldState,
    formState: { errors }
   } = useFormContext()

  const { data, isLoading, isFetching } = useGetInsurersSpecificParams(
    paramsId!,
    null,
    {
      onSuccess: ({ data }) => {
        setValue('individualDiscounts', data)
      },
    },
  )

  const uniqaIndex = data?.data?.findIndex((x) => x.insurer == Insurer.Uniqa)
  const csobIndex = data?.data?.findIndex((x) => x.insurer == Insurer.Csob) ?? -1
  const cppIndex = data?.data?.findIndex((x) => x.insurer == Insurer.CPP) ?? -1
  const koopIndex = data?.data?.findIndex((x) => x.insurer == Insurer.Kooperativa) ?? -1
  const slaviaIndex = data?.data?.findIndex((x) => x.insurer == Insurer.Slavia) ?? -1

  const loading = isLoading || isFetching

  const checkedPillow = insuranceParams?.property.insurers.find((x) => x == Insurer.Pillow)

  const checkedPvzp = insuranceParams?.property.insurers.find((x) => x == Insurer.PVZP)

  const checkedSlavia = insuranceParams?.property.insurers.find((x) => x == Insurer.Slavia)

  const checkedKB = insuranceParams?.property.insurers.find((x) => x == Insurer.KB)

  // platne pro vybrane pojistovny, byt a stavbu (stavba je bud ve vychozi varinte, nebo nektere dalsi variante)
  const showNewPrice = (csobIndex > -1 || cppIndex > -1 || koopIndex > -1) &&
    (insuranceParams?.property.data.realtyTypeId ?? Selection.RodinnyDum) == Selection.Byt && (
    ((insuranceParams?.property.type ?? RiskVariant.Household) == RiskVariant.HouseholdAndRealty ||
        (insuranceParams?.property.type ?? RiskVariant.Household) == RiskVariant.Realty) ||
      (insuranceParams?.property.variants?.findIndex(v =>
        (v.variantParts?.findIndex(vp1 => vp1.risk == Risk.PROPERTY_TYPE &&
          (vp1.riskVariant == RiskVariant.HouseholdAndRealty || vp1.riskVariant == RiskVariant.Realty)) ?? -1) > -1
      ) ?? -1) > -1
  )

  // podmínky: Ne rekreační stavba, a pojištění stavby ve výstavbě
  const isPvzpNewOrInConstrution = (insuranceParams?.property.data.realtyTypeId ?? Selection.RodinnyDum) != Selection.RekreacniObjekt &&
    ((insuranceParams?.property.type ?? RiskVariant.Household) == RiskVariant.HouseholdAndRealty ||
      (insuranceParams?.property.type ?? RiskVariant.Household) == RiskVariant.Realty) &&
    (insuranceParams?.property.data.realty?.state ?? Selection.StavbaDokoncena) == Selection.StavbaVeVystavbe

  const newHomeChecked = isContracted ? 
    insuranceParams?.property.data?.newHome ?? false :
    watch('property.data.newHome')
  const newOrInConstructionChecked = isContracted ? 
    insuranceParams?.property.data?.realty?.newOrInConstruction ?? false :
    watch('property.data.realty.newOrInConstruction')
  const { error: acquiryDateError } = getFieldState('property.data.newHomeAcquiryDate')
  const { error: startConstructionDateError } = getFieldState('property.data.realty.startConstructionDate')
  const { error: endConstructionDateError } = getFieldState('property.data.realty.proposedEndConstructionDate')
  const concurrentInsuranceWithOtherInsurer = watch('property.data.concurrentInsuranceWithOtherInsurer')
  const hasHousehold = insuranceParams?.property.type == RiskVariant.Household || insuranceParams?.property.type == RiskVariant.HouseholdAndRealty
  const hasRealty = insuranceParams?.property.type == RiskVariant.Realty || insuranceParams?.property.type == RiskVariant.HouseholdAndRealty

  return (
    <IndividualDiscountsWrapper>
      {loading && <Loading large={true} />}

      <IndividualDiscountsGroup>
        <FormControl>
          <FormLabel tooltip="Klient souhlasí s valorizací / indexací pojistné částky domácnosti či stavby pojišťovnou.">
            Souhlas s valorizací / indexací pojistné částky
          </FormLabel>

          <SwitchControl>
            <Switch
              {...register('property.data.propertyValorisationAgreement')}
              isChecked={watch('property.data.propertyValorisationAgreement')}
              isDisabled={isContracted}
            />
          </SwitchControl>
        </FormControl>

        {showNewPrice &&
          <FormControl>
            <FormLabel tooltip="Zašktněte, pokud klient preferuje pojištění bytové jednotky v nové ceně. Tuto možnost nabízí pouze pojišťovny ČPP, Kooperativa a ČSOB, ostatní poskytují plnění podle všeobecných pojistných podmínek.">
              Pojištění bytové jednotky na novou cenu
            </FormLabel>

            <SwitchControl>
              <Switch
                {...register('property.data.insuranceInNewPrice')}
                isChecked={watch('property.data.insuranceInNewPrice')}
                isDisabled={isContracted}
              />
            </SwitchControl>
          </FormControl>
        }

        {!loading && (data?.data ?? []).length > 0 && (
          <>
            <IndividualCompany id={Insurer.Csob} name="ČSOB Pojišťovna">
              <FormControl>
                <BaScoringCheckbox
                  isContracted={isContracted}
                  name={`individualDiscounts.${csobIndex}.baScoring`}
                />
              </FormControl>
            </IndividualCompany>

            <IndividualCompany id={Insurer.Uniqa} name="UNIQA">
              <LocalInputGroup
                isInvalid={getFieldState(`individualDiscounts.${uniqaIndex}.contractNumber`)?.invalid}
                id="contract-other-uniqa"
                name="contract-other-uniqa"
                label="Pojistná smlouva u Uniqa"
                tooltip="Zadejte číslo aktivní smlouvy klienta v pojišťovně UNIQA.  Pojišťovna může poskytnout dodatečnou slevu na pojistném."
                optional
                isDisabled={isContracted}
                placeholder="Číslo pojistné smlouvy"
                defaultValue={
                  getValues(
                    `individualDiscounts.${uniqaIndex}.contractNumber`,
                  ) ?? ''
                }
                onChange={(e: any) =>
                  setValue(
                    `individualDiscounts.${uniqaIndex}.contractNumber`,
                    e.target.value,
                    { shouldValidate: true }
                  )
                }
              />
            </IndividualCompany>

            {checkedSlavia && (
              <IndividualCompany id={Insurer.Slavia} name="Slavia pojišťovna">
                <LocalInputGroup
                  id="contract-other-slavia"
                  name="contract-other-slavia"
                  label="Pojistná smlouva u Slavia Pojištovna"
                  tooltip="Zadejte číslo aktivní smlouvy klienta v pojišťovně Slavia. Pojišťovna může poskytnout dodatečnou slevu na pojistném."
                  optional
                  isDisabled={isContracted}
                  placeholder="Číslo pojistné smlouvy"
                  defaultValue={
                    getValues(
                      `individualDiscounts.${slaviaIndex}.contractNumber`,
                    ) ?? ''
                  }
                  onChange={(e: any) =>
                    setValue(
                      `individualDiscounts.${slaviaIndex}.contractNumber`,
                      e.target.value,
                    )
                  }
                />
              </IndividualCompany>
            )}
          </>
        )}

        {checkedPvzp && (
          <IndividualCompany id={Insurer.PVZP} name="Pojišťovna VZP">
            <FormControl>
              <FormLabel tooltip="Pojišťovna poskytuje slevu, pokud se pojišťuje nově pořízený / pronajatý rodinný dům / byt. Podmínkou je, že datum pořízení / pronajmutí není dříve než 2 měsíce před uzavřením smlouvy">
                Nový domov
              </FormLabel>

              <SwitchControl>
                <Switch
                  {...register('property.data.newHome')}
                  isChecked={newHomeChecked}
                  isDisabled={isContracted}
                />
              </SwitchControl>
            </FormControl>

            {newHomeChecked && (
              <FormControl isRequired isInvalid={!!acquiryDateError?.message}>
                <FormLabel>Datum pořízení / pronajmutí domu nebo bytu</FormLabel>

                <ChakraInputGroup>
                  <InputLeftElement pointerEvents="none" children={<Calendar width={4} height={4} />} />
                  <Datepicker
                    isDisabled={isContracted}
                    date={getValues('property.data.newHomeAcquiryDate')}
                    onDateChange={(date) => setValue('property.data.newHomeAcquiryDate', date)}
                  />
                </ChakraInputGroup>
                <FormErrorMessage>{acquiryDateError?.message}</FormErrorMessage>
              </FormControl>
            )}

            {isPvzpNewOrInConstrution && (
              <>
                <FormControl>
                  <FormLabel tooltip="Pojišťovna poskytuje slevu, pokud je pojisťovaná stavba ještě ve výstavbě. Podmínkou je, že měsíc a rok zahájení stavby není dříve jak 36 měsíců před uzavřením smlouvy a předpokládaný měsíc a rok dokončení stavby je později jak 1 měsíc od uzavření smlouvy.">
                    Nová stavba 
                  </FormLabel>

                  <SwitchControl>
                    <Switch
                      {...register('property.data.realty.newOrInConstruction')}
                      isChecked={newOrInConstructionChecked}
                      isDisabled={isContracted}
                    />
                  </SwitchControl>
                </FormControl>

                {newOrInConstructionChecked && (
                  <>
                    <FormControl isRequired isInvalid={!!startConstructionDateError?.message}>
                      <FormLabel>Datum zahájení stavby</FormLabel>
          
                      <ChakraInputGroup>
                        <InputLeftElement pointerEvents="none" children={<Calendar width={4} height={4} />} />
                        <Datepicker
                          isDisabled={isContracted}
                          date={getValues('property.data.realty.startConstructionDate')}
                          onDateChange={(date) => setValue('property.data.realty.startConstructionDate', date)}
                        />
                      </ChakraInputGroup>
                      <FormErrorMessage>{startConstructionDateError?.message}</FormErrorMessage>
                    </FormControl>
                    <FormControl isRequired isInvalid={!!endConstructionDateError?.message}>
                      <FormLabel>Předpokládané datum ukončení stavby</FormLabel>

                      <ChakraInputGroup>
                        <InputLeftElement pointerEvents="none" children={<Calendar width={4} height={4} />} />
                        <Datepicker
                          isDisabled={isContracted}
                          date={getValues('property.data.realty.proposedEndConstructionDate')}
                          onDateChange={(date) => setValue('property.data.realty.proposedEndConstructionDate', date)}
                        />
                      </ChakraInputGroup>
                      <FormErrorMessage>{endConstructionDateError?.message}</FormErrorMessage>
                    </FormControl>
                  </>
                )} 
              </>
            )}
          </IndividualCompany>
        )}

        {checkedPillow && (
          <IndividualCompany id={Insurer.Pillow} name="Pillow">
            <FormControl>
              <Checkbox
                defaultChecked={getValues('property.data.securityFireAlarm')}
                value="1"
                isDisabled={isContracted}
                onChange={(e) =>
                  setValue('property.data.securityFireAlarm', e.target.checked)
                }
              >
                <Flex gap={2}>
                  Požární hlásič
                  <Tooltip
                    hasArrow
                    label="Požární hlásič je kouřový nebo teplotní detektor v centrální části bytu nebo v každém patře rodinného domu. Musí mít akustickou signalizaci a také informovat minimálně dva mobilní telefony."
                    placement="auto"
                  >
                    <Box sx={{ lineHeight: 0 }}>
                      <LabelHintIcon width={4} height={4} />
                    </Box>
                  </Tooltip>
                </Flex>
              </Checkbox>
            </FormControl>

            <FormControl>
              <Checkbox
                defaultChecked={getValues('property.data.securityWaterLeakage')}
                value="1"
                isDisabled={isContracted}
                onChange={(e) =>
                  setValue(
                    'property.data.securityWaterLeakage',
                    e.target.checked,
                  )
                }
              >
                <Flex gap={2}>
                  Detektor úniku vody
                  <Tooltip
                    hasArrow
                    label="Detektor úniku vody umístěný např. pod pračkou, myčkou atd. Musí mít akustickou signalizaci a také informovat minimálně dva mobilní telefony."
                    placement="auto"
                  >
                    <Box sx={{ lineHeight: 0 }}>
                      <LabelHintIcon width={4} height={4} />
                    </Box>
                  </Tooltip>
                </Flex>
              </Checkbox>
            </FormControl>

            <FormControl>
              <Checkbox
                defaultChecked={getValues('property.data.securityBurglarAlarm')}
                value="1"
                isDisabled={isContracted}
                onChange={(e) =>
                  setValue(
                    'property.data.securityBurglarAlarm',
                    e.target.checked,
                  )
                }
              >
                <Flex gap={2}>
                  Alarm vloupání
                  <Tooltip
                    hasArrow
                    label="Detektor pohybu v centrální části bytu nebo detektor otevření všech vstupních dveří se signalizací na min. dva mobilní telefony. Telefony musí být funkční, zapnuté a nacházet se ve vašem dosahu. Alarm musí být v době vloupání ve stavu střežení a musí mít vlastní záložní zdroj. V případě pojistné události musíte doložit záznamy signalizace."
                    placement="auto"
                  >
                    <Box sx={{ lineHeight: 0 }}>
                      <LabelHintIcon width={4} height={4} />
                    </Box>
                  </Tooltip>
                </Flex>
              </Checkbox>
            </FormControl>

            <FormControl>
              <Checkbox
                defaultChecked={getValues(
                  'property.data.securityCentralSecurityDesk',
                )}
                value="1"
                isDisabled={isContracted}
                onChange={(e) =>
                  setValue(
                    'property.data.securityCentralSecurityDesk',
                    e.target.checked,
                  )
                }
              >
                <Flex gap={2}>
                  Pult centralizované ochrany
                  <Tooltip
                    hasArrow
                    label="Pult centralizované ochrany bezpečnostní služby nebo Policie ČR musí být v době vloupání aktivní, zapojen do elektrické sítě vlastním jističem a mít záložní zdroj. Tento poplachový zabezpečovací a tísňový systém musí být instalován firmou s oprávněním k této činnosti a v případě pojistné události musíte doložit zápis o výchozí i periodické revizi alarmu."
                    placement="auto"
                  >
                    <Box sx={{ lineHeight: 0 }}>
                      <LabelHintIcon width={4} height={4} />
                    </Box>
                  </Tooltip>
                </Flex>
              </Checkbox>
            </FormControl>
          </IndividualCompany>
        )}

        {checkedKB && (
          <IndividualCompany id={Insurer.KB} name="KB Pojišťovna">
            <FormControl>
              <Checkbox
                defaultChecked={getValues(
                  'property.data.concurrentInsuranceWithOtherInsurer',
                )}
                value="1"
                isDisabled={isContracted}
                onChange={(e) =>
                  setValue(
                    'property.data.concurrentInsuranceWithOtherInsurer',
                    e.target.checked,
                  )
                }
              >
                <Flex gap={2}>
                  Souběh pojištění
                  <Tooltip
                    hasArrow
                    label="Po dobu souběhu stávajícího pojištění a nového pojištění u KB pojišťovny je její pojištění zdarma. Předpokladem je výběr roční frekvence platby."
                    placement="auto"
                  >
                    <Box sx={{ lineHeight: 0 }}>
                      <LabelHintIcon width={4} height={4} />
                    </Box>
                  </Tooltip>
                </Flex>
              </Checkbox>
            </FormControl>

            {concurrentInsuranceWithOtherInsurer && hasHousehold && (
              <FormControl>
                <FormLabel htmlFor="concurrentHouseholdInsuranceValidTo" 
                  tooltip="Uveďte datum ukončení platnosti stávajícího pojištění domácnosti.">
                  Pojištění domácnosti platné do
                </FormLabel>
      
                <Datepicker isDisabled={isContracted} 
                  errors={(errors as any).property?.data?.concurrentHouseholdInsuranceValidTo}
                  date={getValues('property.data.concurrentHouseholdInsuranceValidTo')} 
                  onDateChange={date => setValue('property.data.concurrentHouseholdInsuranceValidTo', date)}
                  />
              </FormControl>
            )}
            {concurrentInsuranceWithOtherInsurer && hasRealty && (
              <FormControl>
                <FormLabel htmlFor="concurrentRealtyInsuranceValidTo" 
                  tooltip="Uveďte datum ukončení platnosti stávajícího pojištění nemovitosti.">
                  Pojištění nemovitosti platné do
                </FormLabel>
      
                <Datepicker isDisabled={isContracted} 
                  errors={(errors as any).property?.data?.concurrentRealtyInsuranceValidTo} 
                  date={getValues('property.data.concurrentRealtyInsuranceValidTo')} 
                  onDateChange={date => setValue('property.data.concurrentRealtyInsuranceValidTo', date)}
                  />
              </FormControl>
            )}
          </IndividualCompany>
        )}
      </IndividualDiscountsGroup>
    </IndividualDiscountsWrapper>
  )
}
